import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import Header from '../Header';
import Footer from '../Footer';
import ProductCard from '../products/ProductCard';
import { price_range_product, query_products } from '../../store/reducers/homeReducer';
import { messageClear } from '../../store/reducers/cardReducer';
import { DISPLAY, Container, formatQ, Search, Close } from '../home/ui';

/*
 * Listado de la tienda (diseño categoria.html). Lo usan /shops,
 * /products?category= y /products/search?value=: las tres eran copias.
 *
 * Los filtros son los que entiende GET /store/catalog/query: una categoría,
 * rango de precio, calificación mínima, búsqueda y orden por precio. Cada
 * cambio vuelve a la página 1; "Cargar más" pide la siguiente y la agrega.
 */

const RATINGS = [0, 4, 3, 2];
const SORTS = [['', 'Novedades'], ['low-to-high', 'Precio: menor a mayor'], ['high-to-low', 'Precio: mayor a menor']];

const Chevron = ({ open }) => (
    <svg className={`w-[14px] h-[14px] text-[#64748b] transition-transform ${open ? '' : '-rotate-90'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
);
const FilterIcon = () => (
    <svg className='w-4 h-4' viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" /></svg>
);
const GridIcon = () => (
    <svg className='w-[17px] h-[17px]' viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
);
const ListIcon = () => (
    <svg className='w-[17px] h-[17px]' viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="9" y1="6" x2="21" y2="6" /><line x1="9" y1="12" x2="21" y2="12" /><line x1="9" y1="18" x2="21" y2="18" /><circle cx="4" cy="6" r="1" /><circle cx="4" cy="12" r="1" /><circle cx="4" cy="18" r="1" /></svg>
);

const StarsRow = ({ value }) => (
    <span className='inline-flex gap-px text-[15px] leading-none'>
        {[0, 1, 2, 3, 4].map(k => <i key={k} className={`not-italic ${k < value ? 'text-[#fbbf24]' : 'text-[#e2e8f0]'}`}>★</i>)}
    </span>
);

const FilterGroup = ({ title, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className='border-b border-[#f1efea] py-[18px] first:pt-0'>
            <button type='button' onClick={() => setOpen(o => !o)} aria-expanded={open}
                className='flex justify-between items-center w-full text-[13px] font-extrabold tracking-[.08em] uppercase text-[#0f172a]'>
                {title}<Chevron open={open} />
            </button>
            {open && <div className='mt-[14px] flex flex-col gap-1'>{children}</div>}
        </div>
    );
};

const ShopListing = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const category = searchParams.get('category') || '';
    const searchValue = searchParams.get('value') || '';

    const { categorys, priceRange } = useSelector(state => state.home);
    const { errorMessage, successMessage } = useSelector(state => state.card);

    const [sortPrice, setSortPrice] = useState('');
    const [rating, setRating] = useState(0);
    // Precio: null = sin filtro. El borrador sigue al slider y se aplica con un
    // pequeño retraso para no pedir una página por cada píxel.
    const [price, setPrice] = useState(null);
    const [draft, setDraft] = useState(null);
    const [view, setView] = useState('grid');
    const [drawer, setDrawer] = useState(false);

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(12);
    const [loading, setLoading] = useState(true);
    const reqId = useRef(0);

    const lo = Math.floor(priceRange.low || 0);
    const hi = Math.ceil(priceRange.high || 0);

    useEffect(() => { dispatch(price_range_product()); }, []);

    useEffect(() => {
        if (successMessage) { toast.success(successMessage); dispatch(messageClear()); }
        if (errorMessage) { toast.error(errorMessage); dispatch(messageClear()); }
    }, [successMessage, errorMessage]);

    // Filtros → página 1 (reemplaza la lista).
    useEffect(() => { load(1); }, [category, searchValue, sortPrice, rating, price?.[0], price?.[1]]);

    const load = (pageNumber) => {
        const id = ++reqId.current;
        setLoading(true);
        dispatch(query_products({
            category,
            searchValue,
            sortPrice,
            rating,
            // highPrice 0 = sin filtro de precio en el backend.
            low: price ? price[0] : 0,
            high: price ? price[1] : 0,
            pageNumber,
        })).then(({ payload }) => {
            if (id !== reqId.current) return; // respuesta vieja: ya se pidió otra
            const nuevos = payload?.products || [];
            setItems(prev => (pageNumber === 1 ? nuevos : [...prev, ...nuevos]));
            setTotal(payload?.totalProduct || 0);
            setPerPage(payload?.parPage || 12);
            setPage(pageNumber);
            setLoading(false);
        });
    };

    useEffect(() => {
        if (!draft) return;
        const t = setTimeout(() => setPrice(draft[0] <= lo && draft[1] >= hi ? null : draft), 450);
        return () => clearTimeout(t);
    }, [draft?.[0], draft?.[1]]);

    useEffect(() => {
        document.body.style.overflow = drawer ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [drawer]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(key, value); else next.delete(key);
        setSearchParams(next, { replace: true });
    };

    const clearAll = () => {
        setSearchParams({}, { replace: true });
        setRating(0);
        setPrice(null);
        setDraft(null);
    };

    const shown = draft || price || [lo, hi];
    const span = Math.max(1, hi - lo);
    const hasMore = page * perPage < total;
    // Categorías en la barra lateral: las principales siempre, y bajo la
    // elegida su árbol de hijas. Hay nombres repetidos ("Accesorios" bajo
    // Perro y bajo Gato), así que las hijas se eligen por slug.
    const cats = categorys.filter(c => c && c.name);
    const byId = Object.fromEntries(cats.map(c => [c.id, c]));
    const sel = cats.find(c => c.slug === category) || cats.find(c => c.name === category && !c.parentId) || cats.find(c => c.name === category);
    const chain = [];
    for (let c = sel; c; c = byId[c.parentId]) chain.unshift(c);
    const topSel = chain[0];
    const top = cats.filter(c => !c.parentId);
    const hijos = (id) => cats.filter(c => c.parentId === id);
    const arbol = [];
    const recorrer = (id, depth) => hijos(id).forEach(c => { arbol.push({ c, depth }); recorrer(c.id, depth + 1); });
    if (topSel) recorrer(topSel.id, 1);
    const catName = sel?.name || category;

    const active = [];
    if (searchValue) active.push({ key: 'value', label: `“${searchValue}”`, clear: () => setParam('value', '') });
    if (category) active.push({ key: 'category', label: chain.length > 1 ? chain.map(c => c.name).join(' › ') : catName, clear: () => setParam('category', '') });
    if (price) active.push({ key: 'price', label: `${formatQ(price[0])} – ${formatQ(price[1])}`, clear: () => { setPrice(null); setDraft(null); } });
    if (rating) active.push({ key: 'rating', label: `${rating}★ y más`, clear: () => setRating(0) });

    const title = searchValue ? `Resultados para “${searchValue}”` : (catName || 'Toda la tienda');

    const sidebar = (id) => (
        <>
            {top.length > 0 && (
                <FilterGroup title='Categoría'>
                    {[{ c: { id: 'todas', name: 'Todas' }, depth: 0, all: true },
                      ...top.flatMap(t => [{ c: t, depth: 0 }, ...(t.id === topSel?.id ? arbol : [])])
                    ].map(({ c, depth, all }) => (
                        <label key={c.id} className={`flex items-center gap-[10px] py-[7px] pr-2 -mx-2 rounded-[9px] cursor-pointer hover:bg-[#faf9f6] ${depth ? 'text-[13.5px] text-[#334155]' : 'text-[14px] text-[#0f172a]'}`}
                            style={{ paddingLeft: 8 + depth * 18 }}>
                            <input type='radio' name={`categoria-${id}`} checked={all ? !category : sel?.id === c.id}
                                onChange={() => setParam('category', all ? '' : depth ? c.slug : c.name)}
                                className='appearance-none w-[18px] h-[18px] rounded-full border-[1.5px] border-[#e8e6e1] bg-white flex-shrink-0 m-0 cursor-pointer checked:border-[5px] checked:border-[#0891b2]' />
                            {c.name}
                            {(all ? !category : sel?.id === c.id) && !loading && <span className='ml-auto text-[12px] text-[#64748b] font-semibold'>{total}</span>}
                        </label>
                    ))}
                </FilterGroup>
            )}
            <FilterGroup title='Precio'>
                <div className='relative h-7 mt-1'>
                    <div className='absolute left-0 right-0 top-3 h-1 rounded bg-[#e8e6e1]' />
                    <div className='absolute top-3 h-1 rounded bg-[#0891b2]'
                        style={{ left: `${((shown[0] - lo) / span) * 100}%`, right: `${100 - ((shown[1] - lo) / span) * 100}%` }} />
                    {[0, 1].map(i => (
                        <input key={i} type='range' min={lo} max={hi} step={1} value={shown[i]} aria-label={i ? 'Precio máximo' : 'Precio mínimo'}
                            onChange={e => {
                                const v = Number(e.target.value);
                                const d = [...shown];
                                d[i] = i ? Math.max(v, d[0]) : Math.min(v, d[1]);
                                setDraft(d);
                            }}
                            className='shop-range absolute inset-0 w-full appearance-none bg-transparent pointer-events-none m-0' />
                    ))}
                </div>
                <div className='grid grid-cols-[1fr_auto_1fr] gap-2 items-center mt-3'>
                    {[0, 1].map(i => (
                        <React.Fragment key={i}>
                            {i === 1 && <span className='text-[#64748b]'>–</span>}
                            <label className='flex items-center border-[1.5px] border-[#e8e6e1] rounded-[10px] px-[10px] h-10 text-[13px] text-[#64748b] focus-within:border-[#0891b2]'>
                                Q
                                <input inputMode='numeric' aria-label={i ? 'Precio máximo' : 'Precio mínimo'} value={shown[i]}
                                    onChange={e => {
                                        const v = Number(e.target.value.replace(/\D/g, '')) || 0;
                                        const d = [...shown]; d[i] = Math.min(Math.max(v, lo), hi); setDraft(d);
                                    }}
                                    className='border-none outline-none w-full text-[14px] font-bold text-[#0f172a] bg-transparent pl-1' />
                            </label>
                        </React.Fragment>
                    ))}
                </div>
            </FilterGroup>

            <FilterGroup title='Calificación'>
                {RATINGS.map(r => (
                    <label key={r} className='flex items-center gap-2 px-2 py-[7px] -mx-2 rounded-[9px] cursor-pointer text-[14px] text-[#334155] hover:bg-[#faf9f6]'>
                        <input type='radio' name={`rating-${id}`} checked={rating === r} onChange={() => setRating(r)}
                            className='appearance-none w-[18px] h-[18px] rounded-full border-[1.5px] border-[#e8e6e1] bg-white flex-shrink-0 m-0 cursor-pointer checked:border-[5px] checked:border-[#0891b2]' />
                        {r ? <><StarsRow value={r} /><span className='text-[13.5px]'>y más</span></> : <span>Todas</span>}
                    </label>
                ))}
            </FilterGroup>
        </>
    );

    return (
        <div className='w-full bg-white'>
            <Header />
            <style>{`
                .shop-range::-webkit-slider-thumb{-webkit-appearance:none;pointer-events:auto;width:20px;height:20px;border-radius:50%;background:#fff;border:2.5px solid #0891b2;cursor:pointer;box-shadow:0 1px 2px rgba(15,23,42,.04)}
                .shop-range::-moz-range-thumb{pointer-events:auto;width:16px;height:16px;border-radius:50%;background:#fff;border:2.5px solid #0891b2;cursor:pointer}
            `}</style>

            <Container>
                <div className='pt-[18px] pb-7 border-b border-[#f1efea] mb-8'>
                    <nav className='pb-[10px] text-[13px] text-[#64748b] flex gap-2 items-center flex-wrap' aria-label='Ruta'>
                        <Link to='/' className='hover:text-[#0891b2]'>Inicio</Link>
                        <span>/</span>
                        {category || searchValue
                            ? <><Link to='/shops' className='hover:text-[#0891b2]'>Tienda</Link><span>/</span><span className='text-[#0f172a] font-semibold'>{searchValue ? 'Búsqueda' : catName}</span></>
                            : <span className='text-[#0f172a] font-semibold'>Tienda</span>}
                    </nav>
                    <h1 className='font-bold text-[#1e3a8a] tracking-[-.02em] mt-[6px] mb-[6px] leading-[1.05]' style={{ ...DISPLAY, fontSize: 'clamp(28px, 3.2vw, 42px)' }}>{title}</h1>
                    <p className='m-0 text-[#64748b] text-[15px] max-w-[620px]'>
                        {searchValue
                            ? 'Productos que coinciden con tu búsqueda. Afínala con los filtros.'
                            : 'Alimento, accesorios y cuidado para tu peludo. Filtra por categoría, precio o calificación.'}
                    </p>
                </div>

                <div className='grid grid-cols-[270px_minmax(0,1fr)] md-lg:grid-cols-1 gap-10 items-start pb-20'>
                    {/* Filtros: columna fija en escritorio, cajón en móvil */}
                    <aside className='sticky top-[150px] max-h-[calc(100vh-170px)] overflow-y-auto pr-[6px] md-lg:hidden [scrollbar-width:thin] [scrollbar-color:#e8e6e1_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-[#e8e6e1] [&::-webkit-scrollbar-thumb]:rounded'>
                        {sidebar('side')}
                    </aside>
                    <div className={`hidden md-lg:block fixed inset-0 bg-[#0f172a]/40 z-[9990] transition-opacity ${drawer ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setDrawer(false)} />
                    <aside aria-hidden={!drawer} className={`hidden md-lg:block fixed top-0 bottom-0 left-0 w-[min(340px,88vw)] bg-white z-[9991] p-5 overflow-y-auto transition-transform duration-[250ms] shadow-[20px_0_60px_-20px_rgba(15,23,42,.3)] ${drawer ? 'translate-x-0' : '-translate-x-[105%]'}`}>
                        <div className='flex justify-between items-center mb-3 font-bold text-[20px] text-[#1e3a8a]' style={DISPLAY}>
                            Filtros
                            <button type='button' onClick={() => setDrawer(false)} aria-label='Cerrar filtros' className='w-9 h-9 grid place-items-center text-[#64748b]'><Close className='w-5 h-5' /></button>
                        </div>
                        {sidebar('drawer')}
                        <button type='button' onClick={() => setDrawer(false)} className='mt-5 w-full h-[46px] rounded-[12px] bg-[#0891b2] text-white font-bold text-[14px]'>
                            Ver {total} producto{total === 1 ? '' : 's'}
                        </button>
                    </aside>

                    <section>
                        <div className='flex justify-between items-center gap-3 flex-wrap mb-[14px]'>
                            <div className='flex items-center gap-3'>
                                <button type='button' onClick={() => setDrawer(true)}
                                    className='hidden md-lg:inline-flex items-center gap-2 h-[42px] px-4 rounded-[12px] border-[1.5px] border-[#e8e6e1] font-bold text-[14px] text-[#1e3a8a] bg-white'>
                                    <FilterIcon />Filtros
                                    {active.length > 0 && <i className='not-italic bg-[#0891b2] text-white text-[11px] min-w-[20px] h-5 rounded-full grid place-items-center px-1'>{active.length}</i>}
                                </button>
                                <span className='text-[14px] text-[#64748b]'><b className='text-[#0f172a]'>{loading && page === 1 ? '…' : total}</b> productos</span>
                            </div>
                            <div className='flex items-center gap-2'>
                                <select value={sortPrice} onChange={e => setSortPrice(e.target.value)} aria-label='Ordenar'
                                    className='appearance-none h-[42px] border-[1.5px] border-[#e8e6e1] rounded-[12px] pl-[14px] pr-[38px] text-[14px] font-semibold text-[#0f172a] bg-white cursor-pointer outline-none focus:border-[#0891b2]'
                                    style={{ background: `#fff url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 10 10'><path d='M1 3l4 4 4-4' stroke='%231e3a8a' stroke-width='1.5' fill='none' stroke-linecap='round'/></svg>") right 14px center no-repeat` }}>
                                    {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                                <div className='flex border-[1.5px] border-[#e8e6e1] rounded-[12px] overflow-hidden h-[42px]' role='group' aria-label='Vista'>
                                    {[['grid', 'Cuadrícula', GridIcon], ['list', 'Lista', ListIcon]].map(([v, l, Icon]) => (
                                        <button key={v} type='button' onClick={() => setView(v)} aria-label={l} aria-pressed={view === v}
                                            className={`w-[42px] grid place-items-center ${view === v ? 'bg-[#1e3a8a] text-white' : 'text-[#64748b]'}`}>
                                            <Icon />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {active.length > 0 && (
                            <div className='flex gap-2 flex-wrap mb-[22px]'>
                                {active.map(a => (
                                    <span key={a.key} className='inline-flex items-center gap-2 pl-[14px] pr-2 py-[7px] rounded-full bg-[#ecfeff] text-[#0e7490] text-[13px] font-bold'>
                                        {a.label}
                                        <button type='button' onClick={a.clear} aria-label={`Quitar ${a.label}`}
                                            className='w-5 h-5 rounded-full grid place-items-center bg-[#0891b2]/15 text-[13px] leading-none hover:bg-[#0891b2] hover:text-white'>×</button>
                                    </span>
                                ))}
                                {active.length > 1 && (
                                    <button type='button' onClick={clearAll} className='text-[13px] font-bold text-[#64748b] underline px-1 py-[7px] hover:text-[#ef4444]'>Limpiar todo</button>
                                )}
                            </div>
                        )}

                        {loading && page === 1 ? (
                            <div className={`grid gap-[18px] ${view === 'list' ? 'grid-cols-1' : 'grid-cols-3 xl:grid-cols-2 sm:gap-[10px]'}`}>
                                {Array.from({ length: 6 }, (_, i) => (
                                    <div key={i} className='rounded-[18px] border-[1.5px] border-[#f1efea] p-3 animate-pulse'>
                                        <div className='rounded-[14px] bg-[#faf9f6]' style={{ aspectRatio: view === 'list' ? '5 / 1' : '1 / 1' }} />
                                        <div className='h-3 w-2/3 rounded bg-[#f1efea] mt-3' />
                                        <div className='h-4 w-1/3 rounded bg-[#f1efea] mt-3' />
                                    </div>
                                ))}
                            </div>
                        ) : items.length === 0 ? (
                            <div className='text-center px-5 py-[72px] border-[1.5px] border-dashed border-[#e8e6e1] rounded-[22px] bg-[#faf9f6]'>
                                <div className='w-16 h-16 rounded-full bg-white grid place-items-center mx-auto mb-4 text-[#0891b2]'><Search className='w-7 h-7' strokeWidth={2} /></div>
                                {sel && arbol.length > 0 && active.length === 1 ? (
                                    // Una categoría padre sin productos propios: los tiene en sus hijas.
                                    <>
                                        <h3 className='text-[#1e3a8a] text-[22px] font-bold mt-0 mb-[6px]' style={DISPLAY}>Elige una subcategoría</h3>
                                        <p className='text-[#64748b] mt-0 mb-5 text-[14.5px]'>Los productos de {catName.toLowerCase()} están organizados en:</p>
                                        <div className='flex gap-2 flex-wrap justify-center'>
                                            {hijos(sel.id).map(c => (
                                                <button key={c.id} type='button' onClick={() => setParam('category', c.slug)}
                                                    className='px-[18px] py-[10px] rounded-full border-[1.5px] border-[#e8e6e1] bg-white text-[14px] font-bold text-[#0f172a] hover:border-[#0891b2]'>
                                                    {c.name}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                ) : (<>
                                <h3 className='text-[#1e3a8a] text-[22px] font-bold mt-0 mb-[6px]' style={DISPLAY}>No encontramos productos</h3>
                                <p className='text-[#64748b] mt-0 mb-5 text-[14.5px]'>Prueba quitando algún filtro o buscando otra cosa.</p>
                                </>)}
                                {active.length > 0 && !(sel && arbol.length > 0 && active.length === 1) && (
                                    <button type='button' onClick={clearAll} className='h-[46px] px-[22px] rounded-[12px] bg-[#0891b2] hover:bg-[#0e7490] text-white font-bold'>Limpiar filtros</button>
                                )}
                            </div>
                        ) : (
                            <div className={`grid gap-[18px] ${view === 'list' ? 'grid-cols-1' : 'grid-cols-3 xl:grid-cols-2 sm:gap-[10px]'}`}>
                                {items.map(p => <ProductCard key={p._id} product={p} horizontal={view === 'list'} />)}
                            </div>
                        )}

                        {items.length > 0 && (
                            <div className='flex flex-col items-center gap-3 mt-10'>
                                <span className='text-[13px] text-[#64748b]'>Mostrando {items.length} de {total} productos</span>
                                <div className='w-[220px] h-1 rounded bg-[#e8e6e1] overflow-hidden'>
                                    <i className='block h-full bg-[#0891b2]' style={{ width: `${total ? Math.min(100, (items.length / total) * 100) : 0}%` }} />
                                </div>
                                {hasMore && (
                                    <button type='button' onClick={() => load(page + 1)} disabled={loading}
                                        className='h-12 px-7 rounded-[12px] border-[1.5px] border-[#1e3a8a] text-[#1e3a8a] font-bold text-[14px] hover:bg-[#1e3a8a] hover:text-white transition-all disabled:opacity-60'>
                                        {loading ? 'Cargando…' : 'Cargar más productos'}
                                    </button>
                                )}
                            </div>
                        )}
                    </section>
                </div>
            </Container>

            <Footer />
        </div>
    );
};

export default ShopListing;
